import * as v from 'valibot';

// From this amount, regulation requires the donor's document and address, certificate or not.
export const FISCAL_DATA_THRESHOLD = 100;

export const requiresFiscalData = ({
	portalId,
	certificate,
	amount,
}: {
	portalId: string;
	certificate: boolean;
	amount: number | undefined;
}) => certificate || portalId === 'telefonica' || (amount ?? 0) >= FISCAL_DATA_THRESHOLD;

/**
 * The texts the cross-field rules report, passed in already translated.
 *
 * They are a parameter rather than a lingui import so this module stays free of the `msg` macro: it is
 * unit-tested, and importing the macro outside a babel-compiled context throws. The generic constraint
 * messages (required, email, min/max) still come from valibot's global table in `form-i18n.ts` — only
 * these three have no generic action to hang off.
 */
export type DetailsMessages = { required: string; emailsDoNotMatch: string; termsAndConditions: string };

const detailsEntries = {
	firstname: v.pipe(v.string(), v.minLength(1)),
	lastname: v.pipe(v.string(), v.minLength(1)),
	email: v.pipe(v.string(), v.email()),
	repeatEmail: v.pipe(v.string(), v.email()),
	certificate: v.boolean(),
	documentType: v.picklist(['id', 'passport', 'taxId', 'nie', 'others']),
	documentNumber: v.string(),
	streetName: v.string(),
	streetNumber: v.string(),
	additionalInfo: v.union([v.string(), v.undefined()]),
	city: v.string(),
	cp: v.string(),
	country: v.string(),
	region: v.string(),
	province: v.string(),
	termsAndConditions: v.boolean(),
	ngosTermsAndConditions: v.boolean(),
	telefonicaTermsAndConditions: v.boolean(),
	newsletter: v.boolean(),
};

/**
 * The field shapes on their own, for the iframe prefill: it receives whatever a partner posted, so it
 * validates a PARTIAL of this and never the cross-field rules, which only make sense once the donor
 * has filled the form in.
 */
export const detailsObjectSchema = v.object(detailsEntries);

export const detailsSchema = (portalId: string, amount: number | undefined, messages: DetailsMessages) => {
	const fiscalRequired = (certificate: boolean) => requiresFiscalData({ portalId, certificate, amount });
	/** A field the fiscal rules ask for: filled in, or not asked for at all. */
	const requiredWhenFiscal = (value: string, certificate: boolean) => Boolean(value) || !fiscalRequired(certificate);

	return v.pipe(
		v.object(detailsEntries),
		v.forward(
			v.check((value) => value.termsAndConditions, messages.termsAndConditions),
			['termsAndConditions'],
		),
		v.forward(
			v.check((value) => value.repeatEmail === value.email, messages.emailsDoNotMatch),
			['repeatEmail'],
		),
		v.forward(
			v.check((value) => requiredWhenFiscal(value.documentNumber, value.certificate), messages.required),
			['documentNumber'],
		),
		v.forward(
			v.check((value) => requiredWhenFiscal(value.streetName, value.certificate), messages.required),
			['streetName'],
		),
		v.forward(
			v.check((value) => requiredWhenFiscal(value.streetNumber, value.certificate), messages.required),
			['streetNumber'],
		),
		v.forward(
			v.check((value) => requiredWhenFiscal(value.city, value.certificate), messages.required),
			['city'],
		),
		v.forward(
			v.check((value) => requiredWhenFiscal(value.cp, value.certificate), messages.required),
			['cp'],
		),
		v.forward(
			v.check((value) => requiredWhenFiscal(value.country, value.certificate), messages.required),
			['country'],
		),
		// Andorra has no regions or provinces.
		v.forward(
			v.check(
				(value) => value.country === 'AD' || requiredWhenFiscal(value.region, value.certificate),
				messages.required,
			),
			['region'],
		),
		v.forward(
			v.check(
				(value) => value.country === 'AD' || requiredWhenFiscal(value.province, value.certificate),
				messages.required,
			),
			['province'],
		),
		v.forward(
			v.check((value) => value.telefonicaTermsAndConditions || portalId !== 'telefonica', messages.termsAndConditions),
			['telefonicaTermsAndConditions'],
		),
		v.forward(
			v.check((value) => value.ngosTermsAndConditions || !value.certificate, messages.termsAndConditions),
			['ngosTermsAndConditions'],
		),
	);
};

export type DetailsSchema = ReturnType<typeof detailsSchema>;
export type Details = v.InferOutput<typeof detailsObjectSchema>;
